import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Divider, Tag, Typography } from 'src/design-system/components/atoms';
import { customFormatDate } from 'src/utils/utils';
import { TrackingConsignment } from '../GlobalTrack.types';
import { resolveHeadlineStatus } from '../GlobalTrack.utils';
import { useTrackResultsStyles } from '../GlobalTrack.styles';
import TrackingTimeline from '../TrackingTimeline/TrackingTimeline';

interface IProps {
  items: TrackingConsignment[];
  expandedRef: string | null;
  onToggle: (referenceNumber: string) => void;
  hideCourierPartner: boolean;
}

const TrackResults = (props: IProps) => {
  const { items, expandedRef, onToggle, hideCourierPartner } = props;
  const { t, i18n } = useTranslation();
  const classes = useTrackResultsStyles();

  const renderField = (label: string, value?: string | null, nullOption = 'NA') => (
    <div className={classes.field}>
      <Typography.Text className={classes.fieldKey}>{label}</Typography.Text>
      <Typography.Text className={classes.fieldValue}>{value || nullOption}</Typography.Text>
    </div>
  );

  return (
    <div className={classes.list}>
      {items.map((item) => {
        const isExpanded = expandedRef === item.referenceNumber;
        const showCourierTag = !hideCourierPartner && Boolean(item.courierPartnerName);
        const statusLabel = resolveHeadlineStatus(item, i18n.language);

        return (
          <Card key={item.referenceNumber} className={classes.card}>
            <div className={classes.tagRow}>
              {statusLabel ? <Tag className={classes.statusTag}>{statusLabel}</Tag> : null}
              {showCourierTag ? <Tag>{item.courierPartnerName}</Tag> : null}
            </div>

            <div className={classes.fieldRow}>
              {!hideCourierPartner
                ? renderField(t('Partner AWB No.'), item.courierPartnerReferenceNumber, '')
                : null}
              {renderField(t('Customer Reference No.'), item.customerReferenceNumber)}
              {renderField(t('Reference No.'), item.referenceNumber)}
              {item.expectedDeliveryDate
                ? renderField(
                    t('Expected Delivery Date'),
                    customFormatDate(item.expectedDeliveryDate, 'DD MMM YYYY'),
                  )
                : null}
            </div>

            {isExpanded ? (
              <>
                <Divider className={classes.divider} />
                <TrackingTimeline consignment={item} />
              </>
            ) : (
              <div className={classes.detailsRow}>
                <Button
                  type="link"
                  className={classes.detailsLink}
                  onClick={() => onToggle(item.referenceNumber)}
                >
                  {t('View Details')}
                </Button>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default TrackResults;
